import React, { useState } from 'react';
import { Plus, Trash2, X, ChevronDown, ChevronUp, ShieldOff } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Condition operators shared with StepConditionEditor.
 */
const OPERATORS = [
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'not_equals', label: 'does not equal', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'not_contains', label: 'does not contain', needsValue: true },
  { value: 'is_empty', label: 'is empty', needsValue: false },
  { value: 'is_not_empty', label: 'is not empty', needsValue: false },
  { value: 'greater_than', label: 'is greater than', needsValue: true },
  { value: 'less_than', label: 'is less than', needsValue: true },
];

const CONTENT_FIELD_TYPES = ['heading', 'paragraph', 'image'];
const OPTION_FIELD_TYPES = ['select', 'radio', 'checkbox'];

/**
 * Evaluates a single disqualification rule against the respondent's answers.
 *
 * @param {Object} rule - { fieldId, operator, value }
 * @param {Object} responses - Map of fieldId → answer value
 * @returns {boolean}
 */
export function evaluateRule(rule, responses) {
  if (!rule.fieldId || !rule.operator) return false;

  const answer = responses[rule.fieldId];
  const answerStr = answer != null ? String(answer) : '';
  const valueStr = rule.value != null ? String(rule.value) : '';

  switch (rule.operator) {
    case 'equals':
      if (Array.isArray(answer)) return answer.includes(valueStr);
      return answerStr.toLowerCase() === valueStr.toLowerCase();
    case 'not_equals':
      if (Array.isArray(answer)) return !answer.includes(valueStr);
      return answerStr.toLowerCase() !== valueStr.toLowerCase();
    case 'contains':
      if (Array.isArray(answer)) return answer.some(a => String(a).toLowerCase().includes(valueStr.toLowerCase()));
      return answerStr.toLowerCase().includes(valueStr.toLowerCase());
    case 'not_contains':
      if (Array.isArray(answer)) return !answer.some(a => String(a).toLowerCase().includes(valueStr.toLowerCase()));
      return !answerStr.toLowerCase().includes(valueStr.toLowerCase());
    case 'is_empty':
      if (Array.isArray(answer)) return answer.length === 0;
      return !answerStr.trim();
    case 'is_not_empty':
      if (Array.isArray(answer)) return answer.length > 0;
      return !!answerStr.trim();
    case 'greater_than': {
      const a = parseFloat(answerStr), v = parseFloat(valueStr);
      return !isNaN(a) && !isNaN(v) && a > v;
    }
    case 'less_than': {
      const a = parseFloat(answerStr), v = parseFloat(valueStr);
      return !isNaN(a) && !isNaN(v) && a < v;
    }
    default:
      return false;
  }
}

/**
 * Evaluates all disqualification conditions against respondent answers.
 * Conditions use OR logic between them; within a condition, match_type controls AND/OR logic.
 *
 * @param {Array} conditions - Array of disqualification condition objects
 * @param {Object} responses - Map of fieldId → answer value
 * @returns {{ matched: boolean, condition: Object|null }} - The first matched condition or null
 */
export function evaluateDisqualification(conditions, responses) {
  if (!conditions || conditions.length === 0) return { matched: false, condition: null };

  for (const condition of conditions) {
    const rules = condition.rules || [];
    if (rules.length === 0) continue;

    const matchType = condition.match_type || 'any'; // 'all' = AND, 'any' = OR
    let conditionMatched;

    if (matchType === 'all') {
      conditionMatched = rules.every(rule => evaluateRule(rule, responses));
    } else {
      conditionMatched = rules.some(rule => evaluateRule(rule, responses));
    }

    if (conditionMatched) {
      return { matched: true, condition };
    }
  }

  return { matched: false, condition: null };
}

/**
 * Creates a blank disqualification condition.
 */
function createCondition() {
  return {
    id: uuidv4(),
    match_type: 'any',
    rules: [createRule()],
    redirect_url: '',
    message: '',
  };
}

/**
 * Creates a blank rule within a condition.
 */
function createRule() {
  return {
    id: uuidv4(),
    fieldId: '',
    operator: 'equals',
    value: '',
  };
}

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

function RuleRow({ rule, allFields, onUpdate, onRemove, canRemove }) {
  const selectedField = allFields.find(f => f.id === rule.fieldId);
  const operator = OPERATORS.find(o => o.value === rule.operator) || OPERATORS[0];
  const isOptionField = selectedField && OPTION_FIELD_TYPES.includes(selectedField.type);

  return (
    <div className="flex items-start gap-2 group">
      {/* Field selector */}
      <select
        value={rule.fieldId}
        onChange={e => onUpdate({ fieldId: e.target.value, value: '' })}
        className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
      >
        <option value="" disabled>Select a field...</option>
        {allFields.map(f => (
          <option key={f.id} value={f.id}>
            {f.stepTitle ? `${f.stepTitle}: ` : ''}{f.label}
          </option>
        ))}
      </select>

      {/* Operator selector */}
      <select
        value={rule.operator}
        onChange={e => onUpdate({ operator: e.target.value })}
        className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
      >
        {OPERATORS.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      {/* Value input */}
      {operator.needsValue && (
        isOptionField ? (
          <select
            value={rule.value}
            onChange={e => onUpdate({ value: e.target.value })}
            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">Select a value...</option>
            {(selectedField.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            type={selectedField?.type === 'number' ? 'number' : 'text'}
            value={rule.value}
            onChange={e => onUpdate({ value: e.target.value })}
            placeholder="Value..."
            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
          />
        )
      )}
      {!operator.needsValue && <div className="flex-1 min-w-0" />}

      {/* Remove rule button */}
      {canRemove && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove rule"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {!canRemove && <div className="w-5 flex-shrink-0" />}
    </div>
  );
}

function ConditionCard({ condition, index, allFields, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(true);

  const updateRule = (ruleId, updates) => {
    onChange({
      ...condition,
      rules: condition.rules.map(r => r.id === ruleId ? { ...r, ...updates } : r),
    });
  };

  const addRule = () => {
    onChange({ ...condition, rules: [...condition.rules, createRule()] });
  };

  const removeRule = (ruleId) => {
    onChange({ ...condition, rules: condition.rules.filter(r => r.id !== ruleId) });
  };

  const matchTypeLabel = condition.match_type === 'all' ? 'ALL rules match' : 'ANY rule matches';

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-brand-600 dark:hover:text-brand-400"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Condition {index + 1}
          {condition.redirect_url && (
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-1">
              → {condition.redirect_url.length > 40 ? condition.redirect_url.slice(0, 40) + '…' : condition.redirect_url}
            </span>
          )}
        </button>
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          title="Remove condition"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Match type toggle */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Disqualify when</span>
            <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-600">
              {['any', 'all'].map(type => (
                <button
                  key={type}
                  onClick={() => onChange({ ...condition, match_type: type })}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    condition.match_type === type
                      ? 'bg-brand-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {type === 'any' ? 'ANY' : 'ALL'}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {matchTypeLabel}
            </span>
          </div>

          {/* Rules */}
          <div className="space-y-2">
            {condition.rules.map((rule) => (
              <RuleRow
                key={rule.id}
                rule={rule}
                allFields={allFields}
                onUpdate={(updates) => updateRule(rule.id, updates)}
                onRemove={() => removeRule(rule.id)}
                canRemove={condition.rules.length > 1}
              />
            ))}

            {/* Add rule button */}
            {allFields.length > 0 && (
              <button
                onClick={addRule}
                className="flex items-center gap-1.5 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors mt-1"
              >
                <Plus className="w-3 h-3" />
                Add rule
              </button>
            )}

            {allFields.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-1">
                Add fields to your form to create disqualification rules.
              </p>
            )}
          </div>

          {/* Redirect URL */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Redirect URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={condition.redirect_url}
              onChange={e => onChange({ ...condition, redirect_url: e.target.value })}
              placeholder="https://yourdomain.com/not-qualified"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Respondents matching this condition will be redirected here.
            </p>
          </div>

          {/* Optional message */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Message before redirect <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={condition.message}
              onChange={e => onChange({ ...condition, message: e.target.value })}
              placeholder="We're sorry, but you don't meet our current criteria."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400 resize-none"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Shown briefly before the redirect. Leave blank to redirect immediately.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────

/**
 * DisqualificationEditor — Settings UI for configuring respondent disqualification rules.
 *
 * Props:
 *  - conditions: Array of disqualification condition objects (from form.settings.disqualification_conditions)
 *  - steps:      The full steps array (to collect all answerable fields)
 *  - onChange:   (newConditions) => void — called on every change
 */
export default function DisqualificationEditor({ conditions = [], steps = [], onChange }) {
  // Collect all data-entry fields from all steps
  const allFields = (steps || []).flatMap(step =>
    (step.fields || [])
      .filter(f => !CONTENT_FIELD_TYPES.includes(f.type))
      .map(f => ({ ...f, stepTitle: step.title }))
  );

  const addCondition = () => {
    onChange([...conditions, createCondition()]);
  };

  const updateCondition = (id, updated) => {
    onChange(conditions.map(c => (c.id === id ? updated : c)));
  };

  const removeCondition = (id) => {
    onChange(conditions.filter(c => c.id !== id));
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Disqualification</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Redirect respondents to a specific URL based on their answers. Conditions use OR logic — the first match wins.
          </p>
        </div>
      </div>

      {/* Conditions list */}
      {conditions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-center">
          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
            <ShieldOff className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No disqualification conditions</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 max-w-xs">
            Add a condition to redirect respondents who don't meet your criteria to a different page.
          </p>
          <button
            onClick={addCondition}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add condition
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {conditions.map((condition, index) => (
            <ConditionCard
              key={condition.id}
              condition={condition}
              index={index}
              allFields={allFields}
              onChange={(updated) => updateCondition(condition.id, updated)}
              onRemove={() => removeCondition(condition.id)}
            />
          ))}

          {/* Add another condition */}
          <button
            onClick={addCondition}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:border-brand-300 dark:hover:border-brand-500 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add another condition
          </button>
        </div>
      )}
    </div>
  );
}
